<template>
  <Page :title="title" class="c-content-page">
    <section id="container-parent">
      <section
        v-if="!noHeader"
        id="main-content-header"
        ref="mainContentHeaderRef"
        class="sticky top-0 z-1 main-content-header"
      >
        <template v-if="loading">
          <Transition name="fade">
            <div class="pt-4" aria-busy="true">
              <div role="status" aria-label="Loading header content, please wait."></div>
              <Skeleton width="15%" class="mb-3 mx-4" />

              <section id="loading-record-header" class="px-4">
                <Grid class="grid-nogutter">
                  <Col3 class="pr-3">
                    <Skeleton />
                  </Col3>
                </Grid>

                <Grid class="grid-nogutter mt-2">
                  <Col1><Skeleton width="70%" /></Col1>
                  <Col1><Skeleton width="70%" /></Col1>
                </Grid>

                <Grid class="mt-2">
                  <Col1><Skeleton width="70%" /></Col1>
                  <Col1><Skeleton width="70%" /></Col1>
                  <Col1><Skeleton width="70%" /></Col1>
                  <Col1><Skeleton width="70%" /></Col1>
                </Grid>
              </section>
            </div>
          </Transition>
        </template>

        <template v-else>
          <Transition name="fade">
            <div>

              <template v-if="$slots.banner">
                <slot name="banner" />
              </template>

              <template v-if="$slots.breadcrumb">
                <div class="c-page-breadcrumb">
                  <slot name="breadcrumb" />
                </div>
              </template>

              <section id="record-header" class="pt-2 pb-0 px-3 ml-2">
                <div class="flex justify-content-between items-center flex-wrap mb-1">
                  <template v-if="$slots.header || $slots.titleCta || title || actionLabel">
                    <div
                      class="flex justify-content-between align-items-center w-full"
                      :class="title && $slots.header && 'flex-wrap'"
                    >
                      <div
                        v-if="title"
                        :class="{
                          'pb-3 pt-07': !$slots.breadcrumb,
                          'mb-2': $slots.breadcrumb,
                          'flex-grow-1': $slots.header,
                        }"
                      >
                        <div class="flex align-items-center">
                          <h1
                            v-if="title || actionLabel"
                            class="font-semibold text-3xl overflow-wrap-break-word"
                            :class="{
                              'text-overflow-ellipsis overflow-hidden white-space-nowrap':
                                !isMobile,
                              'break-word': isMobile,
                            }"
                            :title="title"
                          >
                            {{ title }}
                          </h1>
                          <slot name="titleCta" v-if="$slots.titleCta" />
                        </div>
                        <template v-if="tagComponents?.length">
                          <div
                            class="flex flex-wrap align-items-center justify-content-start mt-2"
                            style="gap: 0.5rem"
                          >
                            <template
                              v-for="(tagComponent, i) of tagComponents"
                              :key="getTagRenderKey(tagComponent, i)"
                            >
                              <component
                                :is="tagComponent?.tagComponentName"
                                v-bind="tagComponent?.attrs"
                              >
                                {{ tagComponent?.attrs.label }}
                              </component>
                            </template>
                          </div>
                        </template>
                      </div>
                      <div :class="title && $slots.header ? 'flex-none' : 'w-full'">
                        <slot name="header" />
                      </div>
                    </div>
                    <slot name="mobileHeaderSearch"></slot>
                  </template>

                  <template v-else-if="title || actionLabel">
                    <div class="flex flex-column justify-content-start title">
                      <h1
                        class="font-semibold w-8 text-3xl line-height-title text-overflow-ellipsis overflow-hidden white-space-nowrap"
                        :title="title"
                      >
                        {{ title }}
                      </h1>
                    </div>
                  </template>

                  <div class="flex align-items-center justify-content-center actions">
                    <template v-if="$slots.actions">
                      <slot name="actions"></slot>
                    </template>
                    <template v-else>
                      <Button
                        v-if="actionLabel"
                        :label="actionLabel"
                        @click="$emit('action')"
                        :disabled="actionDisabled"
                      />
                    </template>
                  </div>
                </div>
              </section>
              <slot name="tabmenu" />
            </div>
          </Transition>
        </template>

        <template v-if="!$slots.tabmenu">
          <Transition name="fade">
            <div class="w-full border-bottom-1 border-300" />
          </Transition>
        </template>
      </section>

      <Transition name="fade">
        <section class="c-page-container z-0">
          <div class="c-page-content">
            <slot></slot>
          </div>
        </section>
      </Transition>
    </section>
  </Page>
</template>

<script setup lang="ts">
import { ref, type PropType, watch } from 'vue';
import { Page } from './../../../core/containers/pages/page';
import { Button } from '../../../core/buttons/button';
import { Skeleton } from './../../../core/misc';
import { Grid, Col1, Col3 } from '../../../core';
import { type TagComponentProp } from '@centric-os/types';
import { useElementSize } from '@vueuse/core';
import { useContentPageStore } from '@centric-os/stores/content-page/use-content-page-store';
import { useBreakpoints } from '@/helpers/src';
const mainContentHeaderRef = ref();
const { height: mainContentHeaderHeight } = useElementSize(mainContentHeaderRef);

const props = defineProps({
  title: {
    type: String,
  },
  actionLabel: {
    type: String,
  },
  actionDisabled: {
    type: Boolean,
    default: false,
  },
  tagComponents: {
    type: Object as PropType<TagComponentProp[]>,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  noHeader: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['action']);

const getTagRenderKey = (tagComponent: TagComponentProp, index: number): string => {
  return `${tagComponent?.tagComponentName}-${index}`;
};

const contentPageStore = useContentPageStore();

const breakpoints = useBreakpoints();
const isMobile = breakpoints.smaller('md');

watch(
  mainContentHeaderHeight,
  (newVal) => {
    let height = newVal; // Will equate to 0 if props.noHeader is true
    const titleBarHeader: HTMLElement = document.querySelector('#title-bar');
    if (titleBarHeader) {
      height += titleBarHeader?.offsetHeight;
    }
    contentPageStore.contentPageHeaderHeight = height;
  },
  { immediate: props.noHeader },
);
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.main-content-header {
  background-color: $neutralLightColor;
}

.line-height-title {
  color: $shade700;
  line-height: 4rem !important;
}
.pt-07 {
  padding-top: 0.7rem !important;
}

.break-word {
  overflow-wrap: break-word;
}
</style>
